/**
 * Helpers for the current interview's pre/post buffer on the edit
 * calendar.
 *
 * The BE creates real calendar events on the interviewer's calendar for
 * the buffer (`Calendar::BufferEventWorker`, kula/core CORE-4352), titled
 * "Pre-interview buffer: …" / "Post-interview buffer: …". On edit they
 * come back through `/calendar_events` as ordinary busy events — the feed
 * exposes only `subject/start/end/status`, no `is_buffer` marker. So we
 * identify THIS interview's own buffer events by title prefix anchored to
 * the booked-slot boundary, hide them from busy, and instead draw the
 * buffer clearance as the diagonal `bg-slot-buffer` zone (the same design
 * SelfScheduleCalendar paints around its slots).
 */
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import { BG_EVENT_CLASSES, EVENT_PRIORITY } from './eventStyles';

/** BE `AtsInterviewAttendeeBufferEvent::TITLE_PREFIXES`. */
export const PRE_BUFFER_TITLE_PREFIX = 'Pre-interview buffer';
export const POST_BUFFER_TITLE_PREFIX = 'Post-interview buffer';

/** Anchor tolerance — provider round-trips can shift times by seconds. */
const ANCHOR_TOLERANCE_MS = 60_000;

export interface BookedBufferContext {
  bookedStartMs: number;
  bookedEndMs: number;
  toleranceMs?: number;
}

/**
 * True when `evt` is THIS interview's own pre/post buffer block — a
 * "Pre-/Post-interview buffer" event sitting flush against the booked
 * slot (pre ends at booked start; post starts at booked end). Anchored
 * on the boundary rather than the buffer duration, so it still matches
 * when the recruiter edits the minutes in-session before saving (the BE
 * events stay anchored to the unchanged booked time). Other interviews'
 * buffer events sit at different times and stay as legitimate conflicts.
 */
export function isOwnInterviewBufferEvent(
  evt: { title?: string | null; start: string; end: string },
  ctx: BookedBufferContext
): boolean {
  const title = typeof evt.title === 'string' ? evt.title : '';
  if (!title) return false;
  const s = moment(evt.start).valueOf();
  const e = moment(evt.end).valueOf();
  if (!Number.isFinite(s) || !Number.isFinite(e)) return false;
  const tol = ctx.toleranceMs ?? ANCHOR_TOLERANCE_MS;
  if (title.startsWith(PRE_BUFFER_TITLE_PREFIX)) {
    return s < ctx.bookedStartMs && Math.abs(e - ctx.bookedStartMs) <= tol;
  }
  if (title.startsWith(POST_BUFFER_TITLE_PREFIX)) {
    return e > ctx.bookedEndMs && Math.abs(s - ctx.bookedEndMs) <= tol;
  }
  return false;
}

/**
 * Diagonal-stripe clearance zones framing the booked slot with its
 * before/after buffer. Rendered as FOREGROUND events (not `background`)
 * so they sit ABOVE interviewer-busy blocks — FullCalendar caps
 * background events below all foreground events, which buried the wash
 * behind any busy block abutting the booked slot. `priority: bufferZone`
 * (between busy and booked) orders them above busy / below the booked
 * pill; the matching CSS forces the harness full-width and
 * `pointer-events: none` so the wash spans the column and stays
 * non-interactive. Trade-off (product-chosen): a busy block overlapping
 * the buffer is narrowed by FC's column split — accepted so the buffer
 * reads on top like the booked pill.
 */
export function buildBookedBufferZoneEvents(args: {
  bookedStartIso: string;
  bookedEndIso: string;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  timezone: string;
}): EventInput[] {
  const {
    bookedStartIso,
    bookedEndIso,
    bufferBeforeMinutes,
    bufferAfterMinutes,
    timezone,
  } = args;
  const startMs = moment.tz(bookedStartIso, timezone).valueOf();
  const endMs = moment.tz(bookedEndIso, timezone).valueOf();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return [];
  const out: EventInput[] = [];
  if (bufferBeforeMinutes > 0) {
    out.push({
      id: 'booked-buffer-before',
      start: moment
        .tz(startMs - bufferBeforeMinutes * 60_000, timezone)
        .toISOString(),
      end: bookedStartIso,
      classNames: BG_EVENT_CLASSES.slotBuffer,
      color: 'transparent',
      extendedProps: {
        category: 'background',
        priority: EVENT_PRIORITY.bufferZone,
      },
    });
  }
  if (bufferAfterMinutes > 0) {
    out.push({
      id: 'booked-buffer-after',
      start: bookedEndIso,
      end: moment
        .tz(endMs + bufferAfterMinutes * 60_000, timezone)
        .toISOString(),
      classNames: BG_EVENT_CLASSES.slotBuffer,
      color: 'transparent',
      extendedProps: {
        category: 'background',
        priority: EVENT_PRIORITY.bufferZone,
      },
    });
  }
  return out;
}
